/**
 * Voice Timing
 *
 * Lightweight per-turn latency tracker for the XR voice pipeline
 * (record → STT → LLM → TTS → playback). Each `mark()` records a
 * timestamp against a label; `summary()` prints an ordered breakdown.
 *
 * Usage:
 *   const t = new VoiceTiming();
 *   t.mark('recording_stop');
 *   // ... stt ...
 *   t.mark('stt_done');
 *   t.summary();
 */

export interface TimingMark {
    label: string;
    t: number;
    meta?: Record<string, unknown>;
}

export class VoiceTiming {
    private readonly _start: number;
    private readonly _marks: TimingMark[] = [];
    private readonly _tag: string;

    constructor(tag = 'voice') {
        this._start = performance.now();
        this._tag = tag;
        this._marks.push({ label: 'start', t: this._start });
    }

    mark(label: string, meta?: Record<string, unknown>): void {
        this._marks.push({ label, t: performance.now(), meta });
    }

    /** Time elapsed since start (ms) */
    elapsed(): number {
        return performance.now() - this._start;
    }

    /** Emit a one-line summary with per-stage deltas and total */
    summary(): void {
        if (this._marks.length < 2) return;
        const rows: string[] = [];
        for (let i = 1; i < this._marks.length; i++) {
            const prev = this._marks[i - 1];
            const cur = this._marks[i];
            const delta = (cur.t - prev.t).toFixed(0);
            const metaStr = cur.meta
                ? ' ' + Object.entries(cur.meta).map(([k, v]) => `${k}=${String(v)}`).join(' ')
                : '';
            rows.push(`${cur.label}=+${delta}ms${metaStr}`);
        }
        const total = (performance.now() - this._start).toFixed(0);
        console.log(`[${this._tag}] ${rows.join(' | ')} | TOTAL=${total}ms`);
    }
}
